import { useEffect, useState } from 'react';

/**
 * Mobile pieces of the chat workspace that JavaScript must know about
 * (everything else is plain CSS in `chat.css` / `states.css`).
 *
 * UI_REFERENCE_SOURCE: "Sakhtar AI Assistant.dc.html" - mobile frames M1-M4
 * (list as its own page, full-screen conversation, context bottom sheet,
 * out of tokens). The breakpoint is the app shell's own: <= 899px has no
 * sidebar and shows the bottom navigation.
 */

/**
 * Which page of the mobile flow is on screen: the conversation list (M1) or a
 * conversation opened full screen (M2). Desktop always shows both side by side.
 */
export type MobileView = 'list' | 'chat';

/**
 * The value exposed as `data-mobile-view` on `.ai-chat`. The module shell hides
 * its own header / notice / tabs with `.ai-module:has(.ai-chat[data-mobile-view='chat'])`,
 * so the attribute must read `list` everywhere the chat is not full screen.
 */
export function exposedMobileView(isMobile: boolean, view: MobileView): MobileView {
  return isMobile ? view : 'list';
}

/** Elements a Tab key press can land on inside the bottom sheet. */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Focus trap for the sheet: given the index of the focused element among the
 * sheet's `count` focusable elements (-1 when focus is outside / on the sheet
 * container itself), returns the index that must receive focus, or `null` when
 * the browser's own Tab order is already correct (the focus moves inside).
 */
export function trapFocusTarget(index: number, count: number, backwards: boolean): number | null {
  if (count <= 0) return null;
  if (index < 0) return backwards ? count - 1 : 0;
  if (backwards && index === 0) return count - 1;
  if (!backwards && index === count - 1) return 0;
  return null;
}

/**
 * Starts a freshly opened mobile page at its top. The shell scrolls inside its
 * own content container (not the window), so every scrolled ancestor is reset.
 */
export function scrollPageToTop(from: HTMLElement): void {
  for (let node: HTMLElement | null = from; node; node = node.parentElement) {
    if (node.scrollTop > 0) node.scrollTop = 0;
  }
  window.scrollTo({ top: 0 });
}

/**
 * Tracks the mobile visual viewport (iPhone / Android) when the virtual keyboard
 * opens or closes, synchronizing `--ai-visual-height` and setting `data-keyboard-open`
 * on `document.body` so fixed bottom navigation and floating elements step aside.
 */
export function useVisualViewportKeyboard(isMobile: boolean, view: MobileView): boolean {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!isMobile || view !== 'chat' || typeof window === 'undefined') {
      setIsKeyboardOpen(false);
      if (typeof document !== 'undefined') {
        document.body.removeAttribute('data-keyboard-open');
        document.documentElement.style.removeProperty('--ai-visual-height');
      }
      return;
    }

    const vv = window.visualViewport;
    const update = () => {
      if (!vv) return;
      const heightDiff = window.innerHeight - vv.height;
      const open = heightDiff > 120;
      setIsKeyboardOpen(open);
      if (open) {
        document.body.setAttribute('data-keyboard-open', 'true');
        document.documentElement.style.setProperty('--ai-visual-height', `${vv.height}px`);
      } else {
        document.body.removeAttribute('data-keyboard-open');
        document.documentElement.style.removeProperty('--ai-visual-height');
      }
    };

    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    }
    window.addEventListener('focusin', update);
    const onFocusOut = () => {
      setTimeout(update, 80);
    };
    window.addEventListener('focusout', onFocusOut);

    return () => {
      if (vv) {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      }
      window.removeEventListener('focusin', update);
      window.removeEventListener('focusout', onFocusOut);
      document.body.removeAttribute('data-keyboard-open');
      document.documentElement.style.removeProperty('--ai-visual-height');
    };
  }, [isMobile, view]);

  return isKeyboardOpen;
}
